import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { Table, Card, CardBody, Badge, Spinner, Row, Col, Input, Button, Label } from "reactstrap";
import { fetchJournal } from "../../helpers/api/finance";
import PageHeader from "../common/PageHeader";
import FalconCardHeader from "../common/FalconCardHeader";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faListUl, faCalendarDay, faExchangeAlt, faArrowUp, faArrowDown, faSearch, faFilePdf, faPrint } from "@fortawesome/free-solid-svg-icons";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { useReactToPrint } from "react-to-print";
import { useSelector } from "react-redux";
import Logo from "../navbar/Logo";
import ButtonIcon from "../common/ButtonIcon";
import SupportLink from '../root/support/components/SupportLink';
import { BolivarAmount, RateLabel, CalculatedRateNote } from "./BookRate";

const Journal = () => {
    const { id } = useParams();
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isExporting, setIsExporting] = useState(false);
    const reportRef = useRef();
    const { condominium, address, phone } = useSelector((state) => state.condominium);

    // Filtros de fecha
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];

    const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
    const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);

    const loadData = async () => {
        setLoading(true);
        try {
            const data = await fetchJournal(id, startDate, endDate);
            setEntries(data);
        } catch (error) {
            console.error("Error loading journal entries:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, [id]);

    const handleFilter = (e) => {
        e.preventDefault();
        loadData();
    };

    const handlePrint = useReactToPrint({
        contentRef: reportRef,
    });

    const handleDownloadPDF = async () => {
        setIsExporting(true);
        setTimeout(async () => {
            try {
                const element = reportRef.current;

                const canvas = await html2canvas(element, {
                    scale: 2,
                    useCORS: true,
                    logging: false,
                    scrollY: -window.scrollY,
                    windowHeight: element.scrollHeight,
                    height: element.scrollHeight,
                    backgroundColor: '#ffffff'
                });

                const imgData = canvas.toDataURL('image/png');
                const pdf = new jsPDF('p', 'mm', 'a4');

                const pdfWidth = pdf.internal.pageSize.getWidth();
                const pdfHeight = pdf.internal.pageSize.getHeight();

                const imgWidth = pdfWidth;
                const imgHeight = (canvas.height * imgWidth) / canvas.width;

                let heightLeft = imgHeight;
                let position = 0;

                pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
                heightLeft -= pdfHeight;

                while (heightLeft >= 0) {
                    position = heightLeft - imgHeight;
                    pdf.addPage();
                    pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
                    heightLeft -= pdfHeight;
                }

                pdf.save(`Libro_Diario_${startDate}_${endDate}.pdf`);
            } catch (error) {
                console.error("Error generating PDF:", error);
            } finally {
                setIsExporting(false);
            }
        }, 200);
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return {
            full: date.toLocaleDateString("es-ES"),
            day: date.getDate(),
            month: date.toLocaleString('es-ES', { month: 'short' }).toUpperCase().replace('.', '')
        };
    };

    const formatCurrency = (amount) => {
        if (!amount) return "-";
        return new Intl.NumberFormat("es-VE", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(amount);
    };

    return (
        <div className="container-fluid p-0">
            <PageHeader
                title="Libro Diario"
                titleTag="h2"
                description="Registro cronológico y sistemático de todas las transacciones financieras."
                className="mb-3"
                col={{ lg: 9 }}
            >
                <SupportLink />
            </PageHeader>

            <Card className="shadow-none border-0 mb-3">
                <FalconCardHeader title="Libro Diario" titleTag="h3" light={false} className="border-bottom py-2">
                    <div className="d-flex align-items-center justify-content-between flex-wrap">
                        <div className="d-flex align-items-center flex-wrap gap-2 py-2">
                            {/* Grupo de Fechas */}
                            <div className="d-flex align-items-center gap-1 bg-100 p-1 rounded-2 border mr-3">
                                <div className="input-group input-group-sm" style={{ width: '165px' }}>
                                    <span className="input-group-text bg-transparent border-0 text-600 fw-bold fs--2 px-2">DESDE</span>
                                    <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="form-control border-0 bg-transparent fs--1" />
                                </div>
                                <div className="vr text-300" style={{ height: '20px' }}></div>
                                <div className="input-group input-group-sm" style={{ width: '165px' }}>
                                    <span className="input-group-text bg-transparent border-0 text-600 fw-bold fs--2 px-2">HASTA</span>
                                    <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="form-control border-0 bg-transparent fs--1" />
                                </div>
                            </div>

                            {/* Botón Actualizar */}
                            <Button
                                color="primary"
                                onClick={handleFilter}
                                className="px-4 shadow-none fw-bold"
                                style={{ height: '36px' }}
                            >
                                <FontAwesomeIcon icon={faSearch} className="mr-2" />
                                Consultar
                            </Button>
                        </div>

                        {/* Acciones de Exportación */}
                        <div className="d-flex align-items-center gap-2 py-2">
                            <ButtonIcon
                                color="falcon-default"
                                size="sm"
                                icon={faFilePdf}
                                onClick={handleDownloadPDF}
                                disabled={loading || entries.length === 0}
                                className="fs--1 mr-2"
                            >
                                PDF
                            </ButtonIcon>
                            <ButtonIcon
                                color="falcon-default"
                                size="sm"
                                icon={faPrint}
                                onClick={() => handlePrint()}
                                disabled={loading || entries.length === 0}
                                className="fs--1"
                            >
                                Imprimir
                            </ButtonIcon>
                        </div>
                    </div>
                </FalconCardHeader>
                <div ref={reportRef} className="bg-white">
                    {/* Header solo para impresión/PDF */}
                    <div className={`${!isExporting ? 'd-none' : 'd-block'} d-print-block p-4 border-bottom mb-3`}>
                        <Row className="align-items-center">
                            <Col xs={6}>
                                <Logo width={120} to={null} className="justify-content-start" />
                            </Col>
                            <Col xs={6} className="text-right">
                                <h4 className="mb-0 text-primary fw-black">LIBRO DIARIO</h4>
                                <p className="mb-0 fs--1 fw-semi-bold">{condominium}</p>
                                <p className="mb-0 fs--2 text-500">{address}</p>
                                <p className="mb-0 fs--2 text-500">RIF: {phone}</p>
                            </Col>
                        </Row>
                        <hr className="my-3" />
                        <div className="d-flex justify-content-between">
                            <span className="fs--1 text-700">Periodo: <strong>{startDate} / {endDate}</strong></span>
                            <span className="fs--1 text-700">Generado el: {new Date().toLocaleDateString()}</span>
                        </div>
                    </div>
                    <CardBody className="p-0 border-top">
                        <div className="p-3 bg-light d-flex justify-content-between align-items-center">
                            <span className="text-700 fs--1 fw-semi-bold">
                                Mostrando movimientos del periodo seleccionado
                            </span>
                            <Badge color="soft-primary" pill className="px-3">
                                {entries.length} hallados
                            </Badge>
                        </div>
                        {entries.some((entry) => entry.rate?.calculated) && <CalculatedRateNote />}

                        {loading ? (
                            <div className="text-center py-5">
                                <Spinner color="primary" type="grow" size="lg" />
                                <p className="mt-3 text-muted fw-semi-bold">Cargando registros contables...</p>
                            </div>
                        ) : (
                            <div className="table-responsive scrollbar">
                                <Table className="mb-0">
                                    <thead className="bg-200 text-900">
                                        <tr>
                                            <th className="py-1 px-4 border-0 fs--2 text-uppercase text-center" style={{ width: "85px" }}>Código</th>
                                            <th className="py-1 border-0 fs--2 text-uppercase">Descripción / Cuentas</th>
                                            <th className="py-1 text-end border-0 fs--2 text-uppercase" style={{ width: "120px" }}>Debe</th>
                                            <th className="py-1 text-end border-0 fs--2 text-uppercase px-4" style={{ width: "120px" }}>Haber</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {entries.length === 0 ? (
                                            <tr>
                                                <td colSpan="4" className="text-center py-4">
                                                    <div className="py-3">
                                                        <FontAwesomeIcon icon={faListUl} size="2x" className="mb-2 text-200" />
                                                        <p className="text-muted fs--1">No se encontraron movimientos en este periodo.</p>
                                                    </div>
                                                </td>
                                            </tr>
                                        ) : (
                                            entries.map((entry) => {
                                                const dateObj = formatDate(entry.date);
                                                // Número absoluto y estable calculado en el backend (no cambia
                                                // según el rango de fechas filtrado), igual al que se ve en el
                                                // Libro Mayor para el mismo asiento.
                                                const asientoNo = entry.asiento_no;

                                                return (
                                                    <React.Fragment key={entry.id}>
                                                        {/* Cabecera del Asiento */}
                                                        <tr className="bg-light">
                                                            <td className="py-1 px-3 border-y border-200" colSpan="4">
                                                                <div className="d-flex justify-content-between align-items-center">
                                                                    <div className="d-flex align-items-center">
                                                                        <div className="bg-primary text-white px-2 py-0 rounded fs--2 fw-black mr-2">
                                                                            #{asientoNo}
                                                                        </div>
                                                                        <span className="fw-bold text-primary fs--2">
                                                                            ASIENTO CONTABLE
                                                                        </span>
                                                                    </div>
                                                                    <div className="d-flex align-items-center">
                                                                        {entry.iso && (
                                                                            <span className="text-600 fs--2 mr-3">
                                                                                <RateLabel iso={entry.iso} rate={entry.rate} multipleRates={entry.multiple_rates} />
                                                                            </span>
                                                                        )}
                                                                        <span className="text-700 fs--2 fw-black text-uppercase">
                                                                            <FontAwesomeIcon icon={faCalendarDay} className="mr-1 text-400" />
                                                                            {dateObj.full}
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                        </tr>

                                                        {/* Detalle de Cuentas */}
                                                        {entry.details.map((detail, index) => (
                                                            <tr key={`${entry.id}-${index}`} className="border-0">
                                                                <td className="py-1 px-3 align-middle text-center border-0 border-right border-100 bg-soft-light" style={{ width: '85px' }}>
                                                                    <span className="text-700 fs--2 fw-bold">{detail.code}</span>
                                                                </td>
                                                                <td className="py-1 border-0">
                                                                    <div
                                                                        className="d-flex align-items-center"
                                                                        style={{ paddingLeft: detail.side === 'haber' ? '2rem' : '0.5rem' }}
                                                                    >
                                                                        <div className={`fs--1 ${detail.side === 'haber' ? 'text-700 fw-semi-bold' : 'text-900 fw-bold'}`}>
                                                                            {detail.side === 'haber'
                                                                                ? `a ${detail.account}`
                                                                                : (detail.account.toLowerCase().includes('gastos generales') && entry.description
                                                                                    ? entry.description
                                                                                    : detail.account)}
                                                                        </div>
                                                                    </div>
                                                                </td>
                                                                <td className="py-1 text-end text-900 fs--1 align-middle border-0 border-left border-100 fw-bold bg-soft-primary bg-opacity-10" style={{ width: '120px' }}>
                                                                    {detail.side === 'debe' ? formatCurrency(detail.amount) : ''}
                                                                    {detail.side === 'debe' && <BolivarAmount iso={entry.iso} value={detail.amount_bs} />}
                                                                </td>
                                                                <td className="py-1 text-end text-900 fs--1 align-middle px-3 border-0 border-left border-100 fw-bold bg-soft-success bg-opacity-10" style={{ width: '120px' }}>
                                                                    {detail.side === 'haber' ? formatCurrency(detail.amount) : ''}
                                                                    {detail.side === 'haber' && <BolivarAmount iso={entry.iso} value={detail.amount_bs} />}
                                                                </td>
                                                            </tr>
                                                        ))}

                                                        {/* Glosa y Pie de Asiento */}
                                                        <tr className="border-bottom border-200">
                                                            <td className="bg-soft-light border-0 border-right border-100"></td>
                                                            <td colSpan="3" className="py-1 border-0 pb-2">
                                                                <div className="ml-3 d-flex align-items-center">
                                                                    <div className="text-400 fs--2 mr-2 fw-bold text-uppercase">Glosa:</div>
                                                                    <div className="text-500 fs--2 italic line-height-1">
                                                                        {entry.description || "Sin descripción"}
                                                                    </div>
                                                                </div>
                                                            </td>
                                                        </tr>

                                                        {/* Separador */}
                                                        <tr style={{ height: '0.5rem' }}>
                                                            <td colSpan="4" className="border-0 bg-white"></td>
                                                        </tr>
                                                    </React.Fragment>
                                                );
                                            })
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        )}
                    </CardBody>
                </div>
            </Card>

            <style>{`
                .shadow-smooth {
                    box-shadow: 0 0.75rem 1.5rem rgba(18, 38, 63, 0.03);
                }
                .bg-soft-primary {
                    background-color: #f4f6fc !important;
                }
                .fw-black {
                    font-weight: 900;
                }
                .fw-semi-bold {
                    font-weight: 600;
                }
                .border-primary-200 {
                    border-bottom: 2px solid #dee2f6 !important;
                }
                .hover-bg-light:hover {
                    background-color: #f9fafd !important;
                }
            `}</style>
        </div>
    );
};

export default Journal;
